import matchSorter from 'match-sorter';
import { Component } from 'react';
import { findDOMNode } from 'react-dom';
import { DOWN_KEY, ENTER_KEY, ESCAPE_KEY, TAB_KEY, UP_KEY } from '../../utils/keys';
import { ClickAwayListener } from '../ClickAwayListener/ClickAwayListener';
import { FormItem, ValidationState } from '../Form/FormItem';
import { Group } from '../Group/Group';
import { Icon } from '../Icon/Icon';
import { MenuButtonElement } from '../MultiSelect/MultiSelect.styles';
import {
  AutocompleteElement,
  FaceElement,
  InputElement,
  MenuItemElement,
  MenuListElement,
  ButtonElement,
} from './Autocomplete.styles';

export interface IAutocompleteItem {
  id: string;
  label: string;
}

export interface IProps {
  /** Autocomplete label */
  label?: string;
  /** List of items where each item has a string-based `id` and a descriptive `label`. */
  items?: IAutocompleteItem[];
  /** The id of the selected item.  Undefined by default. */
  value?: string;
  /** The value of the input. Undefined by default */
  inputValue?: string;
  /** Event invoked whenever the selected item changes. */
  onSelectedItemChanged?: (value: string) => void;
  /** Event invoked whenever the autocomplete input content changes.  */
  onInputChanged?: (value: string) => void;
  /** Whether the input is initially focused or not.  Defaults to false. */
  autoFocus?: boolean;
  /** Icon for the new button. Defaults to "plus". */
  ButtonIcon?: string;
  /** Event called when the new button is clicked. */
  onButtonClicked?: () => void;
  /** Whether a value is required or not when used in forms. Defaults to false. */
  required?: boolean;
  /** The tab index of the autocomplete input.  Defaults to 0. */
  tabIndex?: number;
  /** The placeholder text shows when no value is specified. Defaults to undefined. */
  placeholder?: string;
  /** Whether to show the menu button or not.  Defaults to true. */
  enableMenuButton?: boolean;
  /** Whether to show the "clear all" button or not.  Defaults to true. */
  enableClearAllButton?: boolean;
  /** Whether to disable the autocomplete input or not.  Defaults to false. */
  disabled?: boolean;
  /** Input validation state (complete, warning)  */
  validationState?: ValidationState;
  /** Input validation message  */
  validationMessage?: string;
  /** A hint can be passed as HTML to explain what the Autocomplete is used for */
  hint?: any;
  /** Optional tooltip heading.  Defaults to undefined. */
  tooltipHeading?: string;
  /** Optional tooltip description.  Defaults to undefined. */
  tooltipDescription?: string;
  /** Optional tooltip width. Defaults to auto */
  tooltipWidth?: string;
  /** A limit can be passed to restrict the number of elements rendered */
  limit?: number;
  /** Event fired when the ENTER key is pressed. */
  onEnterPressed?: () => void;
  /** Height of the menu in pixels. Defaults to 250. */
  menuHeight?: number;
}

export interface IState {
  showMenu: boolean;
  inputValue: string;
  highlightedIndex: number;
  menuListLeftPosition: number;
  menuListTopPosition: number;
}

/** @deprecated Do not use. Use `Select` instead */
export class Autocomplete extends Component<IProps, IState> {
  public static defaultProps: Pick<IProps, 'items' | 'enableMenuButton' | 'enableClearAllButton' | 'menuHeight'> = {
    items: [],
    enableMenuButton: true,
    enableClearAllButton: true,
    menuHeight: 200,
  };

  public state: IState = {
    highlightedIndex: -1,
    inputValue: null,
    showMenu: false,
    menuListLeftPosition: 0,
    menuListTopPosition: 0,
  };
  private inputRef: any;
  private menuItemRefs: any[] = [];

  public constructor(props: IProps) {
    super(props);
    this.state.inputValue = this.props.inputValue;
  }

  public componentDidMount() {
    if (import.meta.env.VITE_ENV === 'poc') {
      // eslint-disable-next-line no-console
      console.warn("'Autocomplete' component has been deprecated. Please use 'Select'");
    }
  }

  public componentDidUpdate(prevProps) {
    if (this.props.inputValue && this.props.inputValue !== prevProps.inputValue) {
      this.setState({
        inputValue: this.props.inputValue,
      });
    }
  }

  public render() {
    const { inputValue } = this.state;
    const selectedItem = this.props.items.find(i => i.id === this.props.value);
    const selectedItemLabel = selectedItem ? selectedItem.label : '';
    const value = inputValue != null ? inputValue : selectedItemLabel;
    const filteredItems = this.getFilteredItems();
    const showMenu = this.state.showMenu && filteredItems.length > 0;
    return (
      <FormItem
        label={this.props.label}
        validationMessage={this.props.validationMessage}
        validationState={this.props.validationState}
        hint={this.props.hint}
        tooltipHeading={this.props.tooltipHeading}
        tooltipDescription={this.props.tooltipDescription}
        tooltipWidth={this.props.tooltipWidth}
      >
        <AutocompleteElement onKeyDown={this.handleKeyDown} data-testid="Autocomplete">
          <FaceElement>
            <InputElement
              data-testid="InputElement"
              value={value}
              required={this.props.required}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={this.props.autoFocus}
              tabIndex={this.props.tabIndex || 0}
              placeholder={this.props.placeholder}
              disabled={this.props.disabled}
              onChange={this.handleInputChanged}
              ref={this.refInput}
            />

            <Group>
              {this.props.enableClearAllButton && (
                <MenuButtonElement
                  onClick={this.handleClearAllButtonClicked}
                  data-testid="ClearAllButton"
                  type="button"
                >
                  <Icon icon="times" size="large" />
                </MenuButtonElement>
              )}
              {this.menuButtonConditions() && (
                <MenuButtonElement onClick={this.handleMenuButtonClicked} data-testid="MenuButton" type="button">
                  <Icon icon="angle-down" size="large" />
                </MenuButtonElement>
              )}
            </Group>
          </FaceElement>
          {showMenu && (
            <ClickAwayListener onClickAway={this.handleClickAway}>
              <MenuListElement data-testid="MenuList" menuHeight={this.props.menuHeight}>
                {filteredItems.map((item, index) => (
                  <MenuItemElement
                    data-testid="MenuItem"
                    tabIndex={-1}
                    key={index}
                    ref={el => (this.menuItemRefs[index] = el)}
                    isHighlighted={index === this.state.highlightedIndex}
                    onClick={() => this.handleMenuItemClicked(item)}
                    onMouseEnter={() => this.handleMouseEnteredItem(index)}
                  >
                    {item.label}
                  </MenuItemElement>
                ))}
              </MenuListElement>
            </ClickAwayListener>
          )}
          {this.props.onButtonClicked && (
            <ButtonElement onClick={this.props.onButtonClicked}>
              <Icon icon={this.props.ButtonIcon || 'plus'} />
            </ButtonElement>
          )}
        </AutocompleteElement>
      </FormItem>
    );
  }

  public focusMenuItem = highlightedIndex => {
    // eslint-disable-next-line react/no-find-dom-node
    const el: HTMLElement = findDOMNode(this.menuItemRefs[highlightedIndex]) as HTMLElement;

    if (el != null) {
      el.focus();
      // This focuses the input directly after the menu item is focused, which
      // works just fine. We're only focusing the menu item to ensure scrolling
      // works with keyboard nav after all.
      this.focus();
    }
  };

  private handleClickAway = e => {
    this.setState({ showMenu: false, highlightedIndex: -1 });
  };

  private handleInputChanged = e => {
    this.setState({
      inputValue: e.target.value,
      highlightedIndex: 0,
      showMenu: true,
    });
    if (this.props.onInputChanged) {
      this.props.onInputChanged(e.target.value);
    }
  };

  private handleMenuItemClicked = item => {
    this.setSelectedItem(item);
  };

  private setSelectedItem(selectedItem) {
    this.setState({ showMenu: false, inputValue: selectedItem.label });
    this.notifyChange(selectedItem.id);
    if (this.props.onInputChanged) {
      this.props.onInputChanged(selectedItem.label);
    }
  }

  private handleMouseEnteredItem = highlightedIndex => {
    this.setState({ highlightedIndex });
  };

  private handleMenuButtonClicked = () => {
    this.setState({
      showMenu: !this.state.showMenu,
    });
  };

  private handleClearAllButtonClicked = () => {
    this.setState({ showMenu: false, inputValue: null });
    if (this.props.onInputChanged) {
      this.props.onInputChanged(null);
    }
    this.notifyChange(null);
    this.focus();
  };

  private notifyChange = value => {
    if (this.props.onSelectedItemChanged) {
      this.props.onSelectedItemChanged(value);
    }
  };

  private getFilteredItems = () => {
    if (this.props.items) {
      const availableItems = matchSorter(this.props.items, this.state.inputValue, {
        keys: ['label'],
        threshold: matchSorter.rankings.CONTAINS,
      });
      if (this.props.limit) {
        return availableItems.slice(0, this.props.limit);
      }
      return availableItems;
    } else {
      return [];
    }
  };

  private focus() {
    if (this.inputRef) {
      this.inputRef.focus();
    }
  }

  private handleKeyDown = e => {
    const filteredItems = this.getFilteredItems();
    switch (e.keyCode) {
      case UP_KEY: {
        e.stopPropagation();
        e.preventDefault();
        const highlightedIndex = this.state.highlightedIndex - 1;
        if (highlightedIndex >= 0) {
          this.setState({ showMenu: true, highlightedIndex });
        }
        this.focusMenuItem(highlightedIndex);
        break;
      }
      case DOWN_KEY: {
        e.stopPropagation();
        e.preventDefault();
        const highlightedIndex = this.state.highlightedIndex + 1;
        if (highlightedIndex < filteredItems.length) {
          this.setState({ showMenu: true, highlightedIndex });
        }
        this.focusMenuItem(highlightedIndex);
        break;
      }
      case TAB_KEY: {
        if (this.state.showMenu && filteredItems.length > 0 && this.state.highlightedIndex !== -1) {
          e.preventDefault();
          this.handleEnterKeyPressed(filteredItems);
          this.focus();
        } else {
          // If menu is not displayed, let TAB key event pass through so it
          // focuses the ClearAllButton.
        }
        break;
      }
      case ENTER_KEY: {
        e.stopPropagation();
        e.preventDefault();
        this.handleEnterKeyPressed(filteredItems);
        break;
      }
      case ESCAPE_KEY: {
        this.setState({
          showMenu: false,
          inputValue: null,
          highlightedIndex: -1,
        });
        break;
      }
    }
  };

  private handleEnterKeyPressed(filteredItems) {
    if (
      this.state.highlightedIndex !== -1 &&
      filteredItems.length > this.state.highlightedIndex &&
      this.state.showMenu
    ) {
      this.setSelectedItem(filteredItems[this.state.highlightedIndex]);
    } else {
      if (this.props.onEnterPressed) {
        this.props.onEnterPressed();
      }
    }
  }

  private menuButtonConditions() {
    const filteredItems = this.getFilteredItems();
    if (
      this.props.enableMenuButton &&
      this.props.items &&
      this.props.items.length > 0 &&
      filteredItems.length > 0 &&
      filteredItems[0].label !== this.state.inputValue
    ) {
      return true;
    } else {
      return false;
    }
  }

  private refInput = (el: any) => (this.inputRef = el);
}
